/**
 * Barcode Scanner Component.
 *
 * Uses the device camera to scan Code 128 barcodes (SKUs). Falls back
 * to manual text input if the camera is unavailable or permission is denied.
 *
 * Requires the html5-qrcode library.
 */

import { useEffect, useRef, useState } from "react";
import { Camera, X, Keyboard } from "lucide-react";
import Button from "@/components/ui/Button";

interface BarcodeScannerProps {
  onScan: (code: string) => void;
  onClose?: () => void;
}

function BarcodeScanner({ onScan, onClose }: BarcodeScannerProps) {
  const scannerRef = useRef<HTMLDivElement>(null);
  const html5QrCodeRef = useRef<unknown>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualInput, setManualInput] = useState("");

  const startScanner = async () => {
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("barcode-reader");
      html5QrCodeRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 280, height: 120 },
          aspectRatio: 1.0,
        },
        (decodedText: string) => {
          scanner.stop().catch(() => {});
          setIsScanning(false);
          onScan(decodedText);
        },
        () => {
          // Ignore scan failures (continuous scanning).
        },
      );
      setIsScanning(true);
      setError(null);
    } catch {
      setError(
        "Camera access denied or unavailable. Use manual entry instead.",
      );
      setManualMode(true);
    }
  };

  const stopScanner = () => {
    const scanner = html5QrCodeRef.current as {
      stop: () => Promise<void>;
    } | null;
    if (scanner) {
      scanner.stop().catch(() => {});
    }
    setIsScanning(false);
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput.trim()) {
      onScan(manualInput.trim());
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-[var(--text-primary)]">
          {manualMode ? "Enter SKU Manually" : "Scan Barcode"}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => {
              if (manualMode) {
                setManualMode(false);
              } else {
                stopScanner();
                setManualMode(true);
              }
            }}
            className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
            title={manualMode ? "Switch to camera" : "Enter manually"}
          >
            {manualMode ? (
              <Camera className="h-5 w-5" />
            ) : (
              <Keyboard className="h-5 w-5" />
            )}
          </button>
          {onClose && (
            <button
              onClick={() => {
                stopScanner();
                onClose();
              }}
              className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {manualMode ? (
        <form onSubmit={handleManualSubmit} className="flex gap-2">
          <input
            type="text"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            placeholder="Enter SKU (e.g., TR-EQ-000001)"
            autoFocus
            className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm focus:border-[var(--border-focus)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
          />
          <Button type="submit" size="sm">
            Look Up
          </Button>
        </form>
      ) : (
        <>
          <div
            id="barcode-reader"
            ref={scannerRef}
            className="overflow-hidden rounded-[var(--radius-md)] border border-[var(--border-default)] bg-black"
            style={{ minHeight: "250px" }}
          />

          {!isScanning && !error && (
            <Button onClick={startScanner} className="w-full">
              <Camera className="h-4 w-4" /> Start Camera
            </Button>
          )}

          {isScanning && (
            <p className="text-center text-sm text-[var(--text-secondary)]">
              Point your camera at a barcode...
            </p>
          )}

          {error && (
            <p className="text-center text-sm text-[var(--text-danger)]">
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default BarcodeScanner;
